import { useState } from "react";
import Grid from "@mui/material/Grid";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import ButtonLoading from "../Button/ButtonLoading";
import ApiClient from "../../services/ApiClient";
import { useSnackbar } from "notistack";
import PageHeadline from "../PageHeadline/PageHeadline";
import PageContentContainer from "../Container/PageContentContainer";
import ContentBox from "../ContentBox/ContentBox";
import { Dropzone } from "../Dropzone/Dropzone";

type QontoPaymentImportSource = "lawfirm" | "legalbird";

type QontoPaymentImportMetrics = {
  totalRows: number;
  relevantPayments: number;
  identifiedPayments: number;
  assignedPayments: number;
};

type QontoPaymentImportResult = Partial<Record<QontoPaymentImportSource, QontoPaymentImportMetrics>>;

interface QontoPaymentImportResponse {
  success: boolean;
  response: QontoPaymentImportResult;
}

const qontoPaymentImportSourceLabels: Record<QontoPaymentImportSource, string> = {
  lawfirm: "Kanzlei",
  legalbird: "Legalbird",
};

const UploadBankPaymentEntries = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImportingQontoPayments, setIsImportingQontoPayments] = useState(false);
  const [qontoPaymentImportResult, setQontoPaymentImportResult] = useState<QontoPaymentImportResult | null>(null);

  const saveFile = async () => {
    if (!files[0]) {
      return;
    }

    setIsLoading(true);

    let formData = new FormData();
    formData.append("file", files[0]);
    let headers = new Headers();
    headers.set("Content-Type", "multipart/form-data");
    try {
      const res = await ApiClient.post("service/bank_payment_entries", {
        headers: headers,
        body: formData,
      });
      enqueueSnackbar(
        `CSV wurde erfolgreich hochgeladen.
              ${res.totalRows} Zeilen, ${res.relevantPayments} relevant, ${res.identifiedPayments} erkannt, ${res.assignedPayments} zugeordnet`,
        {
          variant: "custom",
          isNonInteractive: true,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        }
      );
      setFiles([]);
    } catch (e: any) {
      enqueueSnackbar(e.errors.csv, {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    }

    setIsLoading(false);
  };

  const importQontoPayments = async () => {
    setIsImportingQontoPayments(true);
    setQontoPaymentImportResult(null);
    try {
      const response: QontoPaymentImportResponse = await ApiClient.post("/lb/services/import_qonto");
      if (!response.success) {
        throw new Error("Qonto payment import failed");
      }
      setQontoPaymentImportResult(response.response);
      enqueueSnackbar("Qonto-Zahlungen wurden erfolgreich importiert", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } catch {
      enqueueSnackbar("Qonto-Zahlungen konnten nicht importiert werden", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } finally {
      setIsImportingQontoPayments(false);
    }
  };

  const qontoPaymentImportEntries = qontoPaymentImportResult
    ? (Object.entries(qontoPaymentImportResult) as [QontoPaymentImportSource, QontoPaymentImportMetrics][])
    : [];

  return (
    <PageContentContainer size={"tiny"}>
      <PageHeadline main="Zahlungen hochladen" />
      <ContentBox>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Dropzone
              onChange={(files) => {
                setFiles(files);
              }}
              clearOnUnmount={true}
              acceptedFiles={["text/csv"]}
              filesLimit={1}
              fileObjects={files}
              dropzoneText={"Datei hierher ziehen oder Fläche anklicken"}
              dropRejectMessage={"Die Datei darf höchstens 300 MB groß sein und muss vom Format CSV sein"}
              maxFileSize={300000000}
            />
          </Grid>
          <Grid size={12}>
            <ButtonLoading onClick={saveFile} variant={"contained"} isLoading={isLoading}>
              Zahlungs-CSV hochladen
            </ButtonLoading>
          </Grid>
        </Grid>
      </ContentBox>
      <ContentBox>
        <Grid container spacing={2}>
          <Grid size={12}>
            <ButtonLoading onClick={importQontoPayments} variant={"contained"} isLoading={isImportingQontoPayments}>
              Qonto Zahlungen importieren
            </ButtonLoading>
          </Grid>
          {qontoPaymentImportEntries.length > 0 && (
            <Grid size={12}>
              <TableContainer>
                <Table size={"small"}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Bereich</TableCell>
                      <TableCell align={"right"}>Zeilen</TableCell>
                      <TableCell align={"right"}>Relevant</TableCell>
                      <TableCell align={"right"}>Erkannt</TableCell>
                      <TableCell align={"right"}>Zugeordnet</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {qontoPaymentImportEntries.map(([source, metrics]) => (
                      <TableRow key={source}>
                        <TableCell>{qontoPaymentImportSourceLabels[source]}</TableCell>
                        <TableCell align={"right"}>{metrics.totalRows}</TableCell>
                        <TableCell align={"right"}>{metrics.relevantPayments}</TableCell>
                        <TableCell align={"right"}>{metrics.identifiedPayments}</TableCell>
                        <TableCell align={"right"}>{metrics.assignedPayments}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
        </Grid>
      </ContentBox>
    </PageContentContainer>
  );
};

export default UploadBankPaymentEntries;
